import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'

export function TermsPage() {
    return (
        <div className="min-h-screen">
            <LandingHeader />
            <main className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6">
                <article className="max-w-3xl mx-auto">
                    <header className="mb-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-3">Terms of Use</h1>
                        <p className="text-sm text-text-muted">Last updated: 9 April 2026</p>
                    </header>

                    <div className="space-y-10 text-text-secondary leading-relaxed">
                        <Section title="1. Acceptance">
                            <p>
                                By using <code className="text-accent">endiagram.com</code>, the playground at{' '}
                                <code className="text-accent">playground.endiagram.com</code>, the public API at{' '}
                                <code className="text-accent">api.endiagram.com</code>, or the{' '}
                                <code className="text-accent">@endiagram/mcp</code> NPM package (collectively, the
                                "Service"), you agree to these terms. If you do not agree, do not use the Service.
                            </p>
                        </Section>

                        <Section title="2. The Service">
                            <p>
                                Endiagram is a structural verification engine for concurrent systems, provided
                                free of charge as a developer tool. The{' '}
                                <code className="text-accent">@endiagram/mcp</code> NPM client is published as
                                open source under the MIT licence; the verification engine and web applications
                                that the client connects to are proprietary and operator-owned. There is no
                                contractual service level, no support guarantee, no uptime promise, and no
                                business agreement implied by your use of the Service.
                            </p>
                        </Section>

                        <Section title="3. No warranty">
                            <p>
                                The Service is provided <strong>"as is"</strong>, without warranty of any kind,
                                express or implied, including but not limited to the warranties of merchantability,
                                fitness for a particular purpose, and non-infringement.
                            </p>
                            <p>
                                Endiagram computes structural properties from the graph you describe. Results are
                                deterministic with respect to the input but depend on whether your input
                                accurately models the system you care about. <strong>Verify critical findings
                                independently before acting on them.</strong> Do not rely on Endiagram as the
                                sole source of truth for safety-critical, regulatory, or financial decisions.
                            </p>
                        </Section>

                        <Section title="4. Limitation of liability">
                            <p>
                                To the maximum extent permitted by applicable law, the operator shall not be
                                liable for any direct, indirect, incidental, special, consequential, or punitive
                                damages arising out of or in connection with your use of, or inability to use,
                                the Service — including but not limited to damages from incorrect analysis
                                results, missed deadlocks, undetected bottlenecks, downtime, data loss, or
                                business interruption.
                            </p>
                        </Section>

                        <Section title="5. Intellectual property">
                            <p>
                                You retain all rights to the EN source code you submit to the Service. By
                                submitting source you grant the operator a non-exclusive, worldwide, royalty-free
                                licence to process it for the purpose of providing the Service to you, including
                                logging samples for diagnostic purposes as described in the{' '}
                                <a href="/privacy" className="text-accent hover:underline">privacy policy</a>.
                            </p>
                            <p>
                                The verification engine, language specification, theme system, observability
                                module, web applications, and all other components of the Service (other than
                                the open-source NPM client below) are <strong>proprietary and owned by the
                                operator</strong>. No licence to the engine source code, internal algorithms,
                                or web applications is granted by your use of the Service. You may not
                                reverse-engineer, decompile, or attempt to derive the engine internals.
                            </p>
                            <p>
                                The <code className="text-accent">@endiagram/mcp</code> NPM client is the only
                                component of the Service published as open source. It is licensed under the
                                <strong> MIT licence</strong> in its public repository at{' '}
                                <a
                                    href="https://github.com/dushyant30suthar/endiagram-mcp"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent hover:underline"
                                >
                                    github.com/dushyant30suthar/endiagram-mcp
                                </a>. The MIT licence applies only to that NPM package and not to the
                                engine or web applications it connects to.
                            </p>
                        </Section>

                        <Section title="6. Acceptable use">
                            <p>You may not:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Attempt to overwhelm the Service with excessive requests (DoS, scraping, fuzzing at high rate)</li>
                                <li>Reverse-engineer, probe, or attempt to evade the rate limiting or bot detection</li>
                                <li>Use the Service to process content you do not have the right to process</li>
                                <li>Use the Service in any way that violates applicable law in your jurisdiction</li>
                            </ul>
                        </Section>

                        <Section title="7. Modifications">
                            <p>
                                The operator may modify, suspend, or discontinue any part of the Service at any
                                time without notice. The operator may also update these terms; continued use
                                after a change constitutes acceptance.
                            </p>
                        </Section>

                        <Section title="8. Governing law">
                            <p>
                                These terms are governed by the laws of India. Any dispute arising out of or in
                                connection with these terms is subject to the exclusive jurisdiction of the
                                courts of India. Mandatory consumer protection rights granted to you under EU,
                                UK, or other applicable consumer law are preserved and not affected by this
                                clause.
                            </p>
                        </Section>

                        <Section title="9. Contact">
                            <p>
                                Questions about these terms:{' '}
                                <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">privacy@endiagram.com</a>
                            </p>
                        </Section>
                    </div>
                </article>
            </main>
            <LandingFooter />
        </div>
    )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section>
            <h2 className="text-xl font-semibold text-text mb-3">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    )
}
